import { useRef, useState } from "react";
import { Nfc } from "lucide-react";
import { OrgLogo } from "@/lib/banks";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

const SKINS = {
  navy: "from-[#1F2A44] via-[#2C3B5E] to-[#1F2A44] text-white",
  mint: "from-[#6FA58F] via-[#87B8A5] to-[#5E907C] text-white",
  gold: "from-[#B8954A] via-[#D8BD78] to-[#A88438] text-[#2B2210]",
  black: "from-[#0E0F12] via-[#2A2B30] to-[#0E0F12] text-white",
  rose: "from-[#C98579] via-[#E3A79A] to-[#B9746A] text-white",
  sky: "from-[#6F8DB8] via-[#9DB4D8] to-[#5F7DA8] text-white",
};
const STATUS = { active: "Active", blocked: "Bloquée", expired: "Expirée" };

const Network = ({ n }) => n === "mastercard"
  ? <span className="flex items-center" aria-label="Mastercard"><i className="h-7 w-7 rounded-full bg-[#EB001B]" /><i className="-ml-3 h-7 w-7 rounded-full bg-[#F79E1B] mix-blend-screen" /></span>
  : <span className="font-heading text-2xl font-black italic tracking-tight" aria-label="Visa">VISA</span>;

const Chip = () => (
  <span className="relative block h-8 w-11 overflow-hidden rounded-md bg-gradient-to-br from-[#F3DFA2] via-[#D8BD78] to-[#B8954A] shadow-inner">
    <i className="absolute inset-x-0 top-1/2 h-px bg-black/25" /><i className="absolute inset-y-0 left-1/3 w-px bg-black/25" /><i className="absolute inset-y-0 right-1/3 w-px bg-black/25" />
  </span>
);

const Face = ({ className, children }) => (
  <div className={cn("absolute inset-0 overflow-hidden rounded-2xl bg-gradient-to-br p-5 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.45)] [backface-visibility:hidden]", className)}>
    <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10" />
    <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-white/5" />
    {children}
  </div>
);

const Limit = ({ label, value }) => {
  const { money } = useFormat();
  return <div><div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div><div className="num text-sm font-bold">{money(value)}</div></div>;
};

export const BankCard3D = ({ c }) => {
  const { money } = useFormat();
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [flip, setFlip] = useState(false);
  const skin = SKINS[c.color] || SKINS.mint;
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    setTilt({ x: ((e.clientY - r.top) / r.height - 0.5) * -14, y: ((e.clientX - r.left) / r.width - 0.5) * 18 });
  };
  return (
    <div className="[perspective:1200px]" data-testid={`card-3d-${c.id}`}>
      <button type="button" ref={ref} onMouseMove={move} onMouseLeave={() => setTilt({ x: 0, y: 0 })} onClick={() => setFlip(!flip)} aria-label={flip ? "Voir le recto" : "Voir plafonds et garanties"}
        className={cn("relative block aspect-[1.586] w-full max-w-sm text-left transition-transform duration-500 ease-out [transform-style:preserve-3d]", c.status !== "active" && "grayscale")}
        style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y + (flip ? 180 : 0)}deg)` }} data-testid={`card-flip-${c.id}`}>
        <Face className={skin}>
          <div className="relative flex h-full flex-col">
            <div className="flex items-start justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2"><OrgLogo slug={c.bank_slug} name={c.bank} className="h-7 w-7" /><span className="truncate text-xs font-semibold">{c.bank}</span></span>
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-80">{c.tier}</span>
            </div>
            <div className="mt-auto flex items-center gap-3"><Chip /><Nfc className="h-5 w-5 opacity-70" /></div>
            <div className="num mt-3 text-lg tracking-[0.2em]" data-testid={`card-number-${c.id}`}>•••• •••• •••• {c.last4}</div>
            <div className="mt-2 flex items-end justify-between gap-2">
              <div className="min-w-0"><div className="text-[9px] uppercase tracking-wider opacity-70">Titulaire</div><div className="truncate text-sm font-semibold uppercase">{c.holder}</div></div>
              <div><div className="text-[9px] uppercase tracking-wider opacity-70">Expire</div><div className="num text-sm font-semibold">{c.expiry}</div></div>
              <Network n={c.network} />
            </div>
          </div>
        </Face>
        <Face className={cn(skin, "[transform:rotateY(180deg)]")}>
          <div className="-mx-5 mt-1 h-8 bg-black/70" />
          <div className="relative mt-3 grid grid-cols-3 gap-2">
            <Limit label="Paiement 30 j" value={c.payment_limit} /><Limit label="Retrait 7 j" value={c.withdrawal_limit} /><Limit label="Cotisation/an" value={c.fee_year} />
          </div>
          <div className="relative mt-3 text-[10px] uppercase tracking-wider opacity-70">{c.guarantees.length} garanties incluses</div>
          <p className="relative mt-1 line-clamp-3 text-[11px] leading-snug">{c.guarantees.join(" · ") || "Aucune garantie d'assurance incluse"}</p>
        </Face>
      </button>
      <div className="mt-2 flex max-w-sm items-center gap-2 px-1 text-xs text-muted-foreground">
        <span className={cn("tone-chip rounded-full px-2 py-0.5 font-semibold", c.status === "active" ? "tone-mint" : "tone-rose")} data-testid={`card-status-${c.id}`}>{STATUS[c.status]}</span>
        <span className="flex-1 truncate">{c.label} · {money(c.fee_year)}/an</span>
        <span className="sr-only">Touchez la carte pour la retourner</span>
      </div>
    </div>
  );
};
